import React, { useEffect } from 'react';

import { Box, Divider, Link, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { AppButton, FormInput, FormPasswordInput } from '@/elements';
import { signInSchema } from '@/validations/signIn.schema';

import { useAppDispatch } from '@/store/hooks';
import { toggleSignInLink } from '@/store/slices/headerSlice';

import { AppRoutesEnum } from '@/enum/appRoutes.enum';

interface ILoginForm {
  onSubmit: SubmitHandler<FieldValues>;
  isLoading?: boolean;
}

/**
 * Login form component with modal for entering TFA
 * @component
 * @param    {ILoginForm}   props     Component props
 * @param    {SubmitHandler<FieldValues>}  props.onSubmit  Submit form function
 * @param    {boolean}  props.isLoading
 * @example
 * <LoginForm
 *   onSubmit={onSubmit}
 *   captchaElement={captchaElement}
 * />
 */
export const LoginForm: React.FC<ILoginForm> = ({ isLoading, onSubmit }) => {
  const { t } = useTranslation();
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(signInSchema(t)),
  });
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    dispatch(toggleSignInLink(false));

    return () => {
      dispatch(toggleSignInLink(true));
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          isDisabled={isLoading}
          label={t('login.email.label')}
          name='emailAddress'
          placeholder={t('login.email.placeholder')}
          type='email'
        />

        <FormPasswordInput
          isDisabled={isLoading}
          label={t('login.password.label')}
          name='password'
          placeholder={t('login.password.placeholder')}
        />

        <VStack alignItems='stretch' spacing='lg'>
          <Box fontSize='sm'>
            {t('login.forgotPassword.question')}&nbsp;
            <Link
              as={RouterLink}
              color='app.linkBlue'
              textDecoration='underline'
              to={AppRoutesEnum.requestResetPassword}
            >
              {t('login.forgotPassword.link')}
            </Link>
          </Box>

          <Box textAlign='center'>
            <AppButton
              isDisabled={!isValid || isLoading}
              isLoading={isLoading}
              type='submit'
              variant='primary'
            >
              {t('login.button')}
            </AppButton>
          </Box>

          <Divider />

          <Box fontSize='sm'>
            {t('login.notWithUsYet')},&nbsp;
            <Link
              as={RouterLink}
              color='app.linkBlue'
              textDecoration='underline'
              to={AppRoutesEnum.signup}
            >
              {t('login.createAccount')}
            </Link>
          </Box>
        </VStack>
      </form>
    </FormProvider>
  );
};
